import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { Trans, useTranslation } from "react-i18next"
import { Text, Button, Link, Alert } from "@oysterhr/ui"
import ConnectPaymentMethodLayout from "../layouts/ConnectPaymentMethodLayout"
import { loadStripe, StripeError } from "@stripe/stripe-js"
import { useMemo, useState } from "react"
import * as Sentry from "@sentry/browser"
import { useConfiguredToasts } from "@oysterhr/feature-components"
import useCreatePadCheckoutSession from "./hooks/useCreatePadCheckoutSession"
import { useCompanyPaymentsSettingsContext } from "../contexts/CompanyPaymentsSettingsContext"
import { PaymentEngagementType } from "../enums"

const HC_ARTICLE = "https://support.oysterhr.com/hc/en-us/articles/16088922219537"

const PadLandingPage = () => {
  const [loading, setLoading] = useState(false)
  const { accountAspect: companyId } = useAccountAspect()
  const { displayErrorToast } = useConfiguredToasts()
  const { contractorDirectDebitEnabled } = useCompanyPaymentsSettingsContext()
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/PadLandingPage")

  const { createPadCheckoutSession } = useCreatePadCheckoutSession()
  const stripePromise = useMemo(() => loadStripe(window.gon.stripeOysterHrCaLtdPublishableKey), [])

  const redirectToCheckout = async () => {
    setLoading(true)

    try {
      // Step 1: Create checkout session
      const response = await createPadCheckoutSession({
        variables: { companyId },
      })
      const sessionId = response.data?.createPadCheckoutSession.checkoutSession?.sessionId
      if (!sessionId) {
        throw new Error(
          response.data?.createPadCheckoutSession.errors?.join(", ") ||
            "Failed to create checkout session: No session ID returned",
        )
      }

      // Step 2: Initialize Stripe
      const stripe = await stripePromise
      if (!stripe) {
        throw new Error("Failed to initialize Stripe")
      }

      // Step 3: Redirect to checkout
      const { error } = await stripe.redirectToCheckout({ sessionId })
      if (error) throw error
    } catch (error) {
      Sentry.captureException(error)

      const errorMessage = (error as StripeError)?.message ?? null
      console.error("PAD checkout error:", errorMessage)
      displayErrorToast({
        content: errorMessage || t("errors.unknown"),
      })
    } finally {
      setLoading(false)
    }
  }
  // This is just used to show different FAQ answers
  const resolvedType = useMemo(() => {
    if (!contractorDirectDebitEnabled) {
      return PaymentEngagementType.Fte
    }
    return PaymentEngagementType.Combined
  }, [contractorDirectDebitEnabled])

  return (
    <ConnectPaymentMethodLayout title={t("title")} subtitle={t("subtitle")}>
      <div className="relative w-[720px] rounded-lg border-2 border-gray-200 bg-white p-2 text-left md:p-8">
        <div className="flex flex-col gap-6">
          <div>
            <Text className="text-base font-semibold leading-6">{t("whatDoesPadDo.question")}</Text>
            <Text size="medium" className="mt-2 text-base font-normal leading-6">
              {t("whatDoesPadDo.answer")}
            </Text>
          </div>
          <div>
            <Text className="text-base font-semibold leading-6">
              {t("whichInvoicesWillBePaid.question")}
            </Text>
            <Text size="medium" className="mt-2 text-base font-normal leading-6">
              {t(`whichInvoicesWillBePaid.answer.${resolvedType}`)}
            </Text>
          </div>
        </div>
        <Alert variation="info" className="mt-2">
          <Alert.Icon />
          <Alert.Content>
            <Text size="medium" className="text-base font-normal leading-6">
              {t("alert")}
            </Text>
          </Alert.Content>
        </Alert>

        <div className="my-6">
          <Trans
            t={t}
            i18nKey="details"
            components={{
              a: <Link to={HC_ARTICLE} className="inline-block" target="_blank" />,
            }}
          />
        </div>
        <Button variation="primary" size="md" isLoading={loading} onClick={redirectToCheckout}>
          {t("continue")}
        </Button>
      </div>
    </ConnectPaymentMethodLayout>
  )
}

export default PadLandingPage
